/**
 * Where a user finds the tours: a button in the app header, alongside Profile and Admin.
 *
 * IT IS NOT A FLOATING PILL IN THE CORNER, and that is the whole point of this file's shape. The
 * bottom-right corner of this app is already spoken for on exactly the screens a tour teaches:
 * Office Board's Add Widget FAB (AddWidgetMenu.jsx:182, `fixed bottom-6 right-6`), the service-ticket
 * and form-fill submit bars (ServiceTicketEntry.jsx:427, FormFill.jsx:432, `fixed bottom-0`), the
 * daily-report and report-review action bars (DailyReportEntry.jsx:1528, ReportReview.jsx:428,
 * `sticky bottom-4`) and the toast stack (ToastProvider.jsx:152). A permanently-mounted pill at
 * z 10800 sat on top of all of them and won every z-fight — on a tablet it covered most of the
 * Submit button a foreman presses every day, whether or not he had ever opened a tour.
 *
 * The header is the one slot on the screen nothing else is anchored to, and AppHeader is shared
 * chrome, so this costs ONE mount point rather than a permanent overlay layer. The overlay itself
 * still mounts at App() — it must survive a cross-module navigation, and this does not: the launcher
 * is only an entry point, and every screen that has tours to offer renders a header.
 *
 * It renders NOTHING when the user has no eligible tour. `availableTours` has already dropped every
 * tour whose steps were all filtered away by permission or module, so an empty list here means this
 * person genuinely has nothing to be shown, and a Help button that opens an empty list teaches them
 * the help is broken.
 */
import { useState, useContext } from 'react'
import { GraduationCap, X, Play, RotateCcw, Check } from 'lucide-react'

import { TourContext } from './tourContext'
import { Z as LAYER } from '../styles/zLayers'

// The popup rung — the same one the notification dropdown two elements over sits on, and under the
// tour overlay so an open tour draws over a menu left hanging. See shared/styles/zLayers.js.
const Z = LAYER.popup

export default function TourLauncher() {
  // Deliberately the raw context and not useTour(): AppHeader is rendered on the login and public
  // routes' side of the tree and by tests that mount a screen on its own, and useTour THROWS outside
  // a provider. A help widget must never be the reason the app header dies.
  const tour = useContext(TourContext)
  const [open, setOpen] = useState(false)

  if (!tour) return null
  const { entries, active, startTour, progressFor } = tour
  if (entries.length === 0) return null
  // While a tour is running the overlay owns the screen; a second surface offering to start a
  // different one is noise.
  if (active) return null

  const launch = (tourId, restart) => {
    setOpen(false)
    startTour(tourId, { restart })
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Show me how"
        // AppHeader's own button class, the same one Profile/Admin/Logout use — including
        // .ah-btn-label, which the header hides below 768px so this collapses to an icon exactly
        // like its neighbours instead of pushing the row into a third line on a phone.
        className="ah-btn"
      >
        <GraduationCap size={15} />
        <span className="ah-btn-label">Show me how</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Guided tours"
          className="w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: Z }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-bold text-gray-900">Show me how</h2>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close tours" className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {entries.map(({ tour: t, steps }) => {
              const progress = progressFor(t.id)
              const done = !!(progress && progress.completedAt)
              // Step COUNT comes from the filtered list, not tour.steps — telling a foreman "7 steps"
              // when two of them were dropped for lack of permission is a small lie that makes the
              // progress counter disagree with itself halfway through.
              const at = progress && progress.stepId
                ? steps.findIndex((s) => s.id === progress.stepId) + 1
                : 0
              return (
                <li key={t.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{t.title}</p>
                      {t.summary && <p className="text-xs text-gray-500 mt-0.5">{t.summary}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {done
                          ? <span className="inline-flex items-center gap-1 text-green-600"><Check className="w-3 h-3" /> Finished</span>
                          : at > 1 ? `Paused at step ${at} of ${steps.length}` : `${steps.length} steps`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => launch(t.id, done)}
                      className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-bfs-maroon hover:opacity-90"
                    >
                      {done
                        ? <><RotateCcw className="w-3.5 h-3.5" /> Again</>
                        : at > 1 ? <><Play className="w-3.5 h-3.5" /> Resume</> : <><Play className="w-3.5 h-3.5" /> Start</>}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
